// Status metadata + UI-enforced transition graphs for inquiries and items.
// The backend only enforces the terminal lock (any non-terminal → any status is
// accepted), so the intended sequence lives here.

import { RestaurantInquiryItemStatus, RestaurantInquiryStatus } from 'apis/restaurant-inquiries';

export interface StatusMeta {
  label: string;
  colorScheme: string;
}

// Display labels: humanized Title Case (QUOTATION_READY → "Quotation Ready").
// The enum VALUES sent to the API are untouched — labels are display-only.
export const INQUIRY_STATUS_META: Record<RestaurantInquiryStatus, StatusMeta> = {
  [RestaurantInquiryStatus.DRAFT]: { label: 'Draft', colorScheme: 'gray' },
  [RestaurantInquiryStatus.NEW]: { label: 'New', colorScheme: 'blue' },
  [RestaurantInquiryStatus.PROCESSING]: { label: 'Processing', colorScheme: 'orange' },
  [RestaurantInquiryStatus.QUOTATION_READY]: { label: 'Quotation Ready', colorScheme: 'cyan' },
  [RestaurantInquiryStatus.PARTIALLY_FULFILLED]: { label: 'Partially Fulfilled', colorScheme: 'teal' },
  [RestaurantInquiryStatus.FULFILLED]: { label: 'Fulfilled', colorScheme: 'green' },
  [RestaurantInquiryStatus.UNFULFILLED]: { label: 'Unfulfilled', colorScheme: 'red' },
  [RestaurantInquiryStatus.CANCELLED]: { label: 'Cancelled', colorScheme: 'gray' },
};

export const inquiryStatusMeta = (s: RestaurantInquiryStatus): StatusMeta =>
  INQUIRY_STATUS_META[s] || { label: s, colorScheme: 'gray' };

// Lifecycle: DRAFT → NEW → PROCESSING → QUOTATION_READY → PARTIALLY_FULFILLED
// → FULFILLED/UNFULFILLED. CANCELLED is reachable from any non-terminal state.
// Terminal states (FULFILLED/UNFULFILLED/CANCELLED) have no exits — the backend
// 400s on them anyway.
export const NEXT_INQUIRY_STATUSES: Record<RestaurantInquiryStatus, RestaurantInquiryStatus[]> = {
  [RestaurantInquiryStatus.DRAFT]: [RestaurantInquiryStatus.NEW, RestaurantInquiryStatus.CANCELLED],
  [RestaurantInquiryStatus.NEW]: [RestaurantInquiryStatus.PROCESSING, RestaurantInquiryStatus.CANCELLED],
  [RestaurantInquiryStatus.PROCESSING]: [RestaurantInquiryStatus.QUOTATION_READY, RestaurantInquiryStatus.CANCELLED],
  [RestaurantInquiryStatus.QUOTATION_READY]: [
    RestaurantInquiryStatus.PARTIALLY_FULFILLED,
    RestaurantInquiryStatus.FULFILLED,
    RestaurantInquiryStatus.UNFULFILLED,
    RestaurantInquiryStatus.CANCELLED,
  ],
  [RestaurantInquiryStatus.PARTIALLY_FULFILLED]: [
    RestaurantInquiryStatus.FULFILLED,
    RestaurantInquiryStatus.UNFULFILLED,
    RestaurantInquiryStatus.CANCELLED,
  ],
  [RestaurantInquiryStatus.FULFILLED]: [],
  [RestaurantInquiryStatus.UNFULFILLED]: [],
  [RestaurantInquiryStatus.CANCELLED]: [],
};

// Terminal = no legal exits. Used by the new-match reactivation copy: a match
// on a terminal inquiry prompts ops to reopen it or start a fresh inquiry.
export const isTerminalInquiryStatus = (s: RestaurantInquiryStatus): boolean =>
  (NEXT_INQUIRY_STATUSES[s] ?? []).length === 0;

// Outcome statuses that must carry an explanation: the PATCH :id/status call
// sends a `reason` (stored on the audit event, shown in the activity feed) for
// these targets. Collected via StatusReasonModal before the transition fires.
export const REASON_REQUIRED_INQUIRY_STATUSES: RestaurantInquiryStatus[] = [
  RestaurantInquiryStatus.PARTIALLY_FULFILLED,
  RestaurantInquiryStatus.UNFULFILLED,
  RestaurantInquiryStatus.CANCELLED,
];

export const inquiryStatusRequiresReason = (s: RestaurantInquiryStatus): boolean =>
  REASON_REQUIRED_INQUIRY_STATUSES.includes(s);

// Display labels only — enum values in API payloads are untouched.
export const ITEM_STATUS_META: Record<RestaurantInquiryItemStatus, StatusMeta> = {
  [RestaurantInquiryItemStatus.CREATED]: { label: 'Created', colorScheme: 'gray' },
  [RestaurantInquiryItemStatus.PROCESSING]: { label: 'Processing', colorScheme: 'orange' },
  [RestaurantInquiryItemStatus.QUOTED]: { label: 'Quoted', colorScheme: 'cyan' },
  [RestaurantInquiryItemStatus.FULFILLED]: { label: 'Fulfilled', colorScheme: 'green' },
  [RestaurantInquiryItemStatus.UNFULFILLED]: { label: 'Unfulfilled', colorScheme: 'red' },
};

export const itemStatusMeta = (s?: RestaurantInquiryItemStatus | string): StatusMeta => {
  if (s && ITEM_STATUS_META[s as RestaurantInquiryItemStatus])
    return ITEM_STATUS_META[s as RestaurantInquiryItemStatus];
  // Unknown/legacy values render exactly as the backend sent them.
  return { label: s || '—', colorScheme: 'gray' };
};

// Item lifecycle: CREATED → PROCESSING → QUOTED → FULFILLED/UNFULFILLED, with
// UNFULFILLED reachable from any non-terminal state.
export const NEXT_ITEM_STATUSES: Record<RestaurantInquiryItemStatus, RestaurantInquiryItemStatus[]> = {
  [RestaurantInquiryItemStatus.CREATED]: [
    RestaurantInquiryItemStatus.PROCESSING,
    RestaurantInquiryItemStatus.UNFULFILLED,
  ],
  [RestaurantInquiryItemStatus.PROCESSING]: [
    RestaurantInquiryItemStatus.QUOTED,
    RestaurantInquiryItemStatus.UNFULFILLED,
  ],
  [RestaurantInquiryItemStatus.QUOTED]: [
    RestaurantInquiryItemStatus.FULFILLED,
    RestaurantInquiryItemStatus.UNFULFILLED,
  ],
  [RestaurantInquiryItemStatus.FULFILLED]: [],
  [RestaurantInquiryItemStatus.UNFULFILLED]: [],
};

// Transitions for an item status, tolerating values outside the documented
// enum: staging creates items as DRAFT (doc says CREATED), and later phases may
// add more. Unknown non-terminal statuses get the CREATED transitions so the
// menu never dead-ends; the backend still has the final say.
export const nextItemStatuses = (s?: string): RestaurantInquiryItemStatus[] => {
  if (!s) return [];
  if (s in NEXT_ITEM_STATUSES) return NEXT_ITEM_STATUSES[s as RestaurantInquiryItemStatus];
  return NEXT_ITEM_STATUSES[RestaurantInquiryItemStatus.CREATED];
};

// Label for a raw status string from the audit feed (fromValue/toValue are
// always strings and may be values we don't know yet — fall back to the raw).
export const rawStatusLabel = (v?: string | null): string => {
  if (!v) return '—';
  return (
    INQUIRY_STATUS_META[v as RestaurantInquiryStatus]?.label ||
    ITEM_STATUS_META[v as RestaurantInquiryItemStatus]?.label ||
    v
  );
};
